import React, { useState, useEffect } from 'react';
import { activityLogsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { Activity, Loader2, ChevronDown, User, Calendar, BookOpen, MapPin, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const ActivityFeed = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [expandedLog, setExpandedLog] = useState(null);

  useEffect(() => {
    loadLogs();
  }, [filterType]);

  const loadLogs = async () => {
    try {
      const params = filterType !== 'all' ? { entity_type: filterType } : {};
      const response = await activityLogsAPI.getAll(params);
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'created': return 'bg-emerald-100 text-emerald-700';
      case 'updated': return 'bg-blue-100 text-blue-700';
      case 'deleted': return 'bg-red-100 text-red-700';
      case 'completed': return 'bg-violet-100 text-violet-700';
      case 'generated': return 'bg-amber-100 text-amber-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getEntityIcon = (entityType) => {
    switch (entityType) {
      case 'student': return User;
      case 'lesson': return Calendar;
      case 'coach': return User;
      case 'program': return BookOpen;
      case 'location': return MapPin;
      case 'settings': return Settings;
      default: return Activity;
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Activity Feed</h1>
          <p className="text-slate-500">Track all system activities and changes</p>
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-48" data-testid="filter-activity-type">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Activities</SelectItem>
            <SelectItem value="student">Students</SelectItem>
            <SelectItem value="lesson">Lessons</SelectItem>
            <SelectItem value="coach">Coaches</SelectItem>
            <SelectItem value="program">Programs</SelectItem>
            <SelectItem value="location">Locations</SelectItem>
            <SelectItem value="settings">Settings</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Activity List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-400">No activity logs found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {logs.map((log) => {
                const Icon = getEntityIcon(log.entity_type);
                return (
                  <Collapsible
                    key={log.id}
                    open={expandedLog === log.id}
                    onOpenChange={(open) => setExpandedLog(open ? log.id : null)}
                  >
                    <CollapsibleTrigger className="w-full" data-testid={`activity-log-${log.id}`}>
                      <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-primary shadow-sm">
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="text-left">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-900">{log.user_name}</span>
                              <Badge className={getActionColor(log.action)}>
                                {log.action}
                              </Badge>
                              <span className="text-slate-500">a {log.entity_type}</span>
                            </div>
                            <p className="text-sm text-slate-500">{formatDate(log.created_at)}</p>
                          </div>
                        </div>
                        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${expandedLog === log.id ? 'rotate-180' : ''}`} />
                      </div>
                    </CollapsibleTrigger>
                    
                    <CollapsibleContent>
                      <div className="ml-14 mt-2 p-4 bg-white rounded-lg border border-slate-100">
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-500">Entity Type:</span>
                            <span className="text-slate-900 capitalize">{log.entity_type}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Entity ID:</span>
                            <span className="text-slate-900 font-mono text-xs">{log.entity_id}</span>
                          </div>
                          {log.details && (
                            <div className="flex justify-between">
                              <span className="text-slate-500">Details:</span>
                              <span className="text-slate-900">{log.details}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-slate-500">Performed by:</span>
                            <span className="text-slate-900">{log.user_name}</span>
                          </div>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ActivityFeed;
